import { db, storage, functions } from "../../../firebase";
import { httpsCallable } from "firebase/functions";
import { getIncomingBookingPolicy } from "./incomingBookingAvailability";
import { isValidIncomingBirthDate } from "./incomingPatientDates";
import { collection, doc, getDocs, setDoc, deleteDoc, runTransaction } from "firebase/firestore";
import { ref, uploadBytes, listAll, deleteObject } from "firebase/storage";
import { buildUniqueNoteFileEntries } from "../../../actions/noteFileNames";

export const getIncomingNoteLocation = (patient) => patient.hn
  ? { collectionName: "patients", recordId: patient.hn, folder: "files" }
  : { collectionName: "incoming", recordId: patient.id, folder: "incoming-files" };

export const getIncomingFileKey = (noteId, filename) => JSON.stringify([noteId, filename]);

export const getVisibleIncomingFiles = (patient, notes) => {
  const removed = new Set(patient.removedIncomingFiles || []);
  return notes.flatMap((note) => (Array.isArray(note.filename) ? note.filename : [])
    .filter(Boolean)
    .map((filename) => ({ ...note, filename: [filename], fileKey: getIncomingFileKey(note.id, filename) }))
    .filter((file) => !removed.has(file.fileKey)));
};

// Incoming visibility is independent of patient notes and Storage objects.
export const removeIncomingFile = async (patient, noteId, filename) => {
  const id = patient.id || patient.hn;
  if (!id || !noteId || !filename) throw new Error("Select a saved incoming file to remove.");
  const incomingRef = doc(db, "incoming", id);
  return runTransaction(db, async (transaction) => {
    const saved = await transaction.get(incomingRef);
    if (!saved.exists() || (saved.data().hn || "") !== (patient.hn || "")) {
      throw new Error("Company HN changed. Reopen the incoming patient before removing a file.");
    }
    const removedIncomingFiles = Array.from(new Set([
      ...(saved.data().removedIncomingFiles || []), getIncomingFileKey(noteId, filename),
    ]));
    transaction.update(incomingRef, { removedIncomingFiles });
    return removedIncomingFiles;
  });
};

const validateRequiredBirthDate = (value) => {
  if (!String(value || "").trim()) throw new Error("กรุณากรอกวันเดือนปีเกิดก่อนบันทึก Incoming");
  if (!isValidIncomingBirthDate(value)) throw new Error("กรุณากรอกวันเดือนปีเกิดที่ถูกต้องก่อนบันทึก Incoming");
};

const saveIncomingWithBooking = async (payload) => {
  const result = await httpsCallable(functions, "saveIncomingWithBooking")(payload);
  if (result.data && result.data.bookingError) {
    const error = new Error("The latest booking cannot be rescheduled from Incoming. Its sleep study date has been restored.");
    error.details = { ...result.data.bookingError, incomingId: result.data.incomingId };
    throw error;
  }
};

export const createIncomingRecord = async (profile, incomingId) => {
  validateRequiredBirthDate(profile.dateOfBirth);
  const incomingRef = incomingId ? doc(db, "incoming", incomingId) : doc(collection(db, "incoming"));
  const data = profile.status === "Cancel" ? { ...profile, sleepStudyDate: "" } :
    { ...profile, status: profile.sleepStudyDate ? "Appointed" : "Pending" };
  // All customers use the server's duplicate check before any incoming or
  // booking writes. Keep the same ID when retrying a completed request.
  await saveIncomingWithBooking({ mode: "create", incomingId: incomingRef.id, profile: data });
  return incomingRef.id;
};

const saveNoteFiles = async (location, note, files, saveMetadata) => {
  const noteRef = doc(collection(db, location.collectionName, location.recordId, "note"));
  const entries = buildUniqueNoteFileEntries(files);
  const filePath = (fileName) => `${location.folder}/${location.recordId}/${noteRef.id}/${fileName}`;
  const uploads = await Promise.allSettled(entries.map(({ file, fileName }) =>
    uploadBytes(ref(storage, filePath(fileName)), file)
  ));
  try {
    const failed = uploads.find((result) => result.status === "rejected");
    if (failed) throw failed.reason;
    const data = { ...note, filename: entries.map((entry) => entry.fileName) };
    await saveMetadata(noteRef, data);
    return { ...data, id: noteRef.id };
  } catch (error) {
    await Promise.allSettled(entries.filter((entry, index) => uploads[index].status === "fulfilled")
      .map(({ fileName }) => deleteObject(ref(storage, filePath(fileName)))));
    throw error;
  }
};

export const saveIncomingNote = async (incomingId, note, files) => {
  if (!incomingId) throw new Error("Save the incoming record before adding files.");
  return saveNoteFiles(getIncomingNoteLocation({ id: incomingId }), { ...note, hn: "" }, files, setDoc);
};

export const saveIncomingFiles = async (patient, note, files) => {
  if (!patient.id) throw new Error("Save the incoming record before adding files.");
  return saveNoteFiles(getIncomingNoteLocation(patient), { ...note, hn: patient.hn || "" }, files,
    (noteRef, data) => runTransaction(db, async (transaction) => {
      const incoming = await transaction.get(doc(db, "incoming", patient.id));
      if (!incoming.exists() || (incoming.data().hn || "") !== (patient.hn || "")) {
        throw new Error("Company HN changed. Reopen the incoming patient before adding files.");
      }
      transaction.set(noteRef, data);
    }));
};

export const updateIncomingRecord = async (patient, previous = patient) => {
  validateRequiredBirthDate(patient.dateOfBirth);
  const { id, patientCreated, patientHnChange, patientLinked, removedIncomingFiles, incomingBooking, ...data } = patient;
  const rebooking = previous.status === "Cancel" && data.sleepStudyDate && data.sleepStudyDate !== (previous.sleepStudyDate || "");
  if (data.status === "Cancel" && !rebooking) data.sleepStudyDate = "";
  else if (data.sleepStudyDate && data.sleepStudyDate !== (previous.sleepStudyDate || "")) data.status = "Appointed";
  const birthDateChanged = Object.prototype.hasOwnProperty.call(data, "dateOfBirth") && data.dateOfBirth !== (previous.dateOfBirth || "");
  if (incomingBooking || getIncomingBookingPolicy(patient.refCustomer) || data.status === "Cancel" || birthDateChanged) {
    await saveIncomingWithBooking({ mode: "update", incomingId: id || patient.hn,
      profile: data, hn: patient.hn || "", previousStudyDate: previous.sleepStudyDate || "",
      previousDateOfBirth: previous.dateOfBirth || "",
      previousBookingPath: (previous.incomingBooking && previous.incomingBooking.path) || "" });
    return;
  }
  const incomingRef = doc(db, "incoming", id || patient.hn);
  await runTransaction(db, async (transaction) => {
    const saved = await transaction.get(incomingRef);
    if (!saved.exists() || (saved.data().hn || "") !== (patient.hn || "")) {
      throw new Error("Company HN changed. Reopen the incoming patient before saving.");
    }
    if ((saved.data().sleepStudyDate || "") !== (previous.sleepStudyDate || "")) {
      throw new Error("Sleep study date changed in Booking Calendar. Reopen the incoming patient before saving.");
    }
    if (Object.prototype.hasOwnProperty.call(data, "dateOfBirth") && (saved.data().dateOfBirth || "") !== (previous.dateOfBirth || "")) {
      throw new Error("Date of birth changed. Reopen the incoming patient before saving.");
    }
    transaction.update(incomingRef, data);
    // Newly registered profiles stay in sync. Linking an existing patient does
    // not grant incoming demographics authority over that established profile.
    if (patient.hn && saved.data().patientLinked !== true) transaction.update(doc(db, "patients", patient.hn), data);
  });
};

export const deleteIncomingRecord = async (patient) => {
  const id = patient.id || patient.hn;
  if (patient.incomingBooking) {
    await saveIncomingWithBooking({ mode: "delete", incomingId: id, hn: patient.hn || "",
      previousStudyDate: patient.sleepStudyDate || "", previousBookingPath: patient.incomingBooking.path });
  }
  if (!patient.hn) {
    const notes = await getDocs(collection(db, "incoming", id, "note"));
    for (const note of notes.docs) {
      const files = await listAll(ref(storage, `incoming-files/${id}/${note.id}`));
      await Promise.all(files.items.map((file) => deleteObject(file)));
      await deleteDoc(note.ref);
    }
  }
  if (!patient.incomingBooking) await deleteDoc(doc(db, "incoming", id));
};
